/**
 * Company: SPHMedia
 * Description: [Custom React Hook for managing sticky inline video player functionality]
 */
import stickyController from "@helper/stickyController";
import { isClientSide } from "@helper/utils";
import { RefObject, useEffect } from "react";

type useStickyInlinePlayerProps = {
  observerRef: RefObject<HTMLDivElement | null>;
  playerId: string;
  isPlaying: boolean;
  isMute: boolean;
  setIsSticky: (value: boolean) => void;
  pipContainerRef?: RefObject<HTMLDivElement | null>;
  inlineContainerRef?: RefObject<HTMLDivElement | null>;
  playerWrapperRef?: RefObject<HTMLDivElement | null>;
  isSticky?: boolean;
};

/**
 * Custom React Hook: useStickyInlinePlayerHook
 * Description: [Custom hook for managing sticky inline video player functionality]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useStickyInlinePlayer({
  pipContainerRef,
  inlineContainerRef,
  playerWrapperRef,
  observerRef,
  playerId,
  isMute,
  isPlaying,
  isSticky,
  setIsSticky,
}: useStickyInlinePlayerProps) {
  useEffect(() => {
    if (isMute && isSticky) {
      setIsSticky(false);
      stickyController.clear(playerId);
    }
  }, [isMute, isSticky, setIsSticky, playerId]);

  useEffect(() => {
    if (!isClientSide) return;
    if (!window.matchMedia("(min-width: 1024px)").matches) return;
    if (!observerRef.current) return;

    const element = observerRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick =
          !isMute &&
          entry.intersectionRatio <= 0.5 &&
          (isPlaying || isSticky === true);

        if (shouldStick) {
          stickyController.setActiveSticky(playerId);
        }

        setIsSticky(
          shouldStick && stickyController.getActiveSticky() === playerId,
        );
      },
      {
        threshold: [0.5],
        rootMargin: "-67px 0px 0px 0px",
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isMute, isPlaying, setIsSticky, observerRef, playerId, isSticky]);

  useEffect(() => {
    const handleStickyChanged = (e: CustomEvent<{ playerId: string }>) => {
      if (e.detail.playerId === playerId) {
        return;
      }

      setIsSticky(false);
    };

    window.addEventListener(
      "stickyPlayerChanged",
      handleStickyChanged as EventListener,
    );

    return () => {
      window.removeEventListener(
        "stickyPlayerChanged",
        handleStickyChanged as EventListener,
      );
    };
  }, [playerId, setIsSticky]);

  useEffect(() => {
    const playerWrapper = playerWrapperRef?.current;

    if (!playerWrapper) {
      return;
    }

    if (isSticky) {
      pipContainerRef?.current?.replaceChildren(playerWrapper);
    } else {
      inlineContainerRef?.current?.appendChild(playerWrapper);
    }
  }, [isSticky, inlineContainerRef, pipContainerRef, playerWrapperRef]);
}
